@let messages = messageStore.messages;
@let languages = languageStore.languages;
@if (messageStore.isLoading() && languageStore.isLoading()) {
  <div class="pt-32">
    <aw-loading-animation></aw-loading-animation>
  </div>
}
@if (
  messages() &&
  !messageStore.isLoading() &&
  languages() &&
  !languageStore.isLoading()
) {
  <aw-section testIdAffix="allMessages">
    <aw-section-header>
      <aw-section-headline>
        {{ cmsTextProvider.userFeedback.headline }}
      </aw-section-headline>
      <aw-section-header-actions>
        <aw-button
          [id]="'save-messages'"
          [type]="'submit'"
          [isDisabled]="!hasChanges"
          (click)="save()"
          [text]="cmsTextProvider.shared.save"
          [style]="'primary'"></aw-button>
        <aw-button
          [id]="'cancel-messages'"
          [type]="'button'"
          [isDisabled]="!hasChanges"
          (click)="reload()"
          [text]="cmsTextProvider.shared.cancel"
          [style]="'primary-alt'"></aw-button>
      </aw-section-header-actions>
    </aw-section-header>
    <div class="flex flex-col gap-4">
      <div
        class="flex items-center gap-4"
        [attr.data-testid]="'texts' | appendTestId: 'filter'">
        <div
          [formGroup]="filterForm"
          [attr.data-testid]="'messages' | appendTestId: 'filter'">
          <aw-text-input
            id="search-filter"
            [placeholder]="cmsTextProvider.userFeedback.searchPlaceholder"
            formControlName="search"></aw-text-input>
        </div>
        <div
          class="flex items-center gap-4"
          [attr.data-testid]="'messages' | appendTestId: 'filter'">
          {{ cmsTextProvider.shared.shownTranslations }}
          @for (language of languages(); track $index) {
            @if (language.isActive) {
              @if (isActiveLanguages(language)) {
                <div
                  (click)="toggleLanguage(language)"
                  [style.border-color]="'rgba(75, 142, 207, 0.30)'"
                  [style.background]="'#F3F7FC'"
                  [attr.data-testid]="language.prefix + '-chip'"
                  [attr.aria-label]="'chip-' + language.prefix"
                  class="relative flex w-fit cursor-pointer gap-2 rounded-lg border p-2 text-lg">
                  <span class="material-symbols-rounded"> check </span>
                  {{ language.name }}
                  @let count = getChangedCountForLang(language);
                  @if (count > 0) {
                    <span
                      class="absolute -top-3 -right-3 rounded-full bg-red-600 px-2 py-1 text-xs font-bold text-red-100">
                      {{ count }}
                    </span>
                  }
                </div>
              } @else {
                <div
                  (click)="toggleLanguage(language)"
                  [attr.data-testid]="language.prefix + '-chip'"
                  [attr.aria-label]="'chip-' + language.prefix"
                  class="relative flex w-fit cursor-pointer gap-2 rounded-lg border p-2 text-lg">
                  {{ language.name }}
                  @let count = getChangedCountForLang(language);
                  @if (count > 0) {
                    <span
                      class="absolute -top-3 -right-3 rounded-full bg-red-600 px-2 py-1 text-xs font-bold text-red-100">
                      {{ count }}
                    </span>
                  }
                </div>
              }
            }
          }
        </div>
        <div class="flex w-1/3 justify-end gap-2">
          <input
            #fileInput
            hidden
            type="file"
            accept="text/csv"
            (change)="onFileSelected($event)" />
          <aw-button
            [id]="'import-texts'"
            [text]="cmsTextProvider.allTexts.import"
            [type]="'button'"
            (click)="fileInput.click()"
            [style]="'primary-alt'" />
          <aw-button
            [id]="'export-texts'"
            [text]="cmsTextProvider.allTexts.export"
            [style]="'primary-alt'"
            (click)="exportMessages()"
            [type]="'button'" />
        </div>
      </div>
      <aw-table
        [isLoading]="messageStore.isLoading()"
        id="ticket-table"
        [data]="filteredMessages"
        [header]="header"
        tone="default"
        [columns]="columns"
        (pageChange)="filterForm.updateValueAndValidity()">
        <ng-template #header let-sortBy="sortBy">
          <aw-table-header
            cellName="code"
            [sortParams]="{
              attribute: 'code',
              compare: messageCodeFilter,
            }"
            class="w-72 overflow-hidden"
            [attr.data-testid]="'messages' | appendTestId: 'header-code'"
            (onSort)="sortBy($event)">
            {{ cmsTextProvider.userFeedback.code }}
          </aw-table-header>
          @for (language of activeLanguages; track $index) {
            <aw-table-header
              [cellName]="language.name"
              [sortParams]="{
                attribute: language.name,
                compare: messageValueFilter,
                language: language.prefix,
              }"
              class="w-full overflow-hidden"
              [attr.data-testid]="
                'messages'
                  | appendTestId
                    : 'header-value-' + language.name.toLocaleLowerCase()
              "
              (onSort)="sortBy($event)">
              {{ language.name }}
            </aw-table-header>
          }
          <aw-table-header
            cellName="lastEdited"
            [sortParams]="{
              attribute: 'lastEditedAt',
              compare: dateLastEditedAtFilter,
            }"
            [attr.data-testid]="
              'messages' | appendTestId: 'header-lastEditedAt'
            "
            class="w-96 overflow-hidden"
            (onSort)="sortBy($event)">
            {{ cmsTextProvider.shared.lastEditedAt }}
          </aw-table-header>
        </ng-template>
        <ng-template #columns let-data="data">
          <aw-table-cell
            cellName="code"
            [attr.data-testid]="'messages' | appendTestId: 'code-' + data.id">
            {{ data.code }}
          </aw-table-cell>
          @for (language of activeLanguages; track $index) {
            @if (isEditing(data, language)) {
              <aw-table-cell
                [cellName]="language.name"
                class="bg-standard-select w-full justify-start"
                [attr.data-testid]="
                  'messages' | appendTestId: 'value-' + data.id
                "
                (click)="startEdit(data, language)">
                <div class="flex w-full flex-col">
                  <div class="relative px-3 py-1">
                    <input
                      autofocus
                      type="text"
                      class="h-11 w-full rounded-md pr-8"
                      [attr.data-testid]="
                        'messages' | appendTestId: 'value-' + data.id
                      "
                      [ngModel]="getLanguageValue(data, language)"
                      (ngModelChange)="onValueChange($event, data, language)"
                      (blur)="stopEdit()"
                      (keydown.enter)="stopEdit()" />
                    <span
                      class="material-symbols-rounded absolute top-3 right-5 cursor-pointer"
                      (click)="stopEdit()">
                      close
                    </span>
                  </div>
                  @if (isTemplateCode(data.code)) {
                    @let attributes = getTemplateCode(data.code);
                    <div class="px-3">
                      {{ cmsTextProvider.shared.availableAttributes }}
                    </div>
                    <div class="px-3 pt-1 pb-3">
                      @for (attribute of attributes; track $index) {
                        - {{ '{{' + attribute + '}}' }}<br />
                      }
                    </div>
                  }
                </div>
              </aw-table-cell>
            }
            @if (!isEditing(data, language)) {
              <aw-table-cell
                [cellName]="language.name"
                class="group flex w-full justify-between py-2 hover:bg-[#E4EEF8]"
                [style.font-weight]="
                  getLanguageIsChanged(data, language) ? 'bold' : 'normal'
                "
                [attr.data-testid]="
                  'messages' | appendTestId: 'value-' + data.id
                ">
                {{ getLanguageValue(data, language) }}
                <span
                  class="material-symbols-rounded cursor-pointer opacity-0 transition-opacity group-hover:opacity-100"
                  (click)="startEdit(data, language)">
                  edit
                </span>
              </aw-table-cell>
            }
          }
          <aw-table-cell
            cellName="lastEdited"
            class="w-full justify-end"
            [attr.data-testid]="
              'messages' | appendTestId: 'lastEditedAt-' + data.id
            ">
            {{ data.text.lastEditedAt | date: DATE_FORMAT_LONG }}
          </aw-table-cell>
        </ng-template>
      </aw-table>
    </div>
  </aw-section>
}
